import React, { type HTMLInputTypeAttribute } from 'react';
import { Controller, type RegisterOptions, useFormContext } from 'react-hook-form';
import { Field, Switch, Flex } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';

type FormSwitchProps = FormFieldInterface & {
	registerOptions?: Partial<RegisterOptions>;
	type?: HTMLInputTypeAttribute;
	switchProps?: Partial<Switch.RootProps>;
	children?: React.ReactNode;
};

export const FormSwitch: React.FC<FormSwitchProps> = props => {
	const { name, label, isRequired, registerOptions, switchProps, helperText, children, ...rest } = props;
	const { t } = useTranslation();
	const { control } = useFormContext();

	return (
		<Controller
			control={control}
			name={name}
			rules={{
				...(isRequired && { required: t('general.required') }),
				...registerOptions,
			}}
			render={({ field, fieldState }) => (
				<Field.Root invalid={fieldState.invalid} required={isRequired} {...rest}>
					<Flex alignItems="center" gap={2}>
						<Switch.Root
							colorPalette={'brand.primary'}
							id={name}
							value={name}
							onChange={field.onChange}
							ref={field.ref}
							checked={field.value as boolean}
							onBlur={field.onBlur}
							{...switchProps}
						>
							<Switch.HiddenInput />
							<Switch.Control>
								<Switch.Thumb />
							</Switch.Control>
							{label && <Switch.Label>{label}</Switch.Label>}
						</Switch.Root>
						{children}
					</Flex>
					{helperText && <Field.HelperText>{helperText}</Field.HelperText>}
					<Field.ErrorText>{fieldState.error?.message}</Field.ErrorText>
				</Field.Root>
			)}
		/>
	);
};
